import {Initializing} from "../../../common/elements";
import {ManagingResources} from "../../../common/lifetime";
import {html, LitElement, type TemplateResult} from "lit";
import {property, state} from "lit/decorators.js";
import {schedule} from "../../../common/utils/promises";
import {ENTRY_COUNT_QUERY_PARAMETER_NAME, NEW_SEARCH_PHRASE_EVENT} from "./constants";
import {resolve} from "../../../container";
import {SearchFacade} from "./searchFacade";
import {QueryParameters} from "../../../common/queryParameters";
import {EventBus} from "../../../common/eventBus";
import {Dictionary} from "../../../page/elements/dictionary";
import type {Maybe, Nullable} from "../../../bootstrap/common/types";
import {trackingButtonInteract} from "../../../tracking/trackingAttributes";

const RESULT_COUNT_PLACEHOLDER = "[COUNT]";

export abstract class Search extends Initializing(ManagingResources(LitElement)) {
    protected dictionary: Dictionary;

    @property({attribute: "show-more", type: Boolean})
    protected showMore: boolean;
    @property({attribute: "language"})
    protected language: string;
    @property({attribute: "number", type: Number})
    protected entriesPerPage: number;
    @property({attribute: "character-limit", type: Number})
    protected characterLimit: number;
    @property({attribute: "archive", type: Boolean})
    protected archive: boolean;
    @state()
    protected hasMore: boolean;
    @state()
    protected resultsText: string;

    protected constructor(
        protected emptyOnEmptySearchPhrase: boolean,
        protected searchFacade: SearchFacade = resolve(SearchFacade),
        protected queryParameters: QueryParameters = resolve(QueryParameters),
        protected eventBus: EventBus = resolve(EventBus)
    ) {
        super();
        this.hasMore = false;
        this.resultsText = "";
    }

    public connectedCallback(): void {
        super.connectedCallback();

        this.dictionary = Dictionary.of(this);

        this.searchFacade.configure(this.language, this.entriesPerPage, this.characterLimit, this.archive, this.emptyOnEmptySearchPhrase);
        this.searchFacade.onUpdateEntryCount(count => this.updateEntryCount(count));
        this.searchFacade.onNewEntries(newEntries => this.updateMore(newEntries.hasMoreEntries));

        this.eventBus.on(NEW_SEARCH_PHRASE_EVENT, event => {
            void schedule(this.nextSearchPhrase(event.data.searchPhrase)).as("do-search");
        }, this);

        void schedule(this.initSearch()).as("init-search");
    }

    protected renderMoreButton(): Nullable<TemplateResult> {
        if (!this.showMore || !this.hasMore) {
            return null;
        }

        const fetchMoreLabel = this.dictionary.translate("CONTENT_SEARCH_FETCH_MORE");
        return html`
            <div class="fetch-more-button">
                <button type="button" @click=${this.fetchMore} class="uni-button primary" ${trackingButtonInteract(fetchMoreLabel)}>
                    ${fetchMoreLabel}
                </button>
            </div>`;
    }

    protected fetchMore(): void {
        void schedule(this.searchFacade.fetchMore()).as("do-search");
    }

    protected renderResultsLabel(): Nullable<TemplateResult> {
        return html`
            <div class="results-label">
                ${this.resultsText}
                <eop-search-filter-tags></eop-search-filter-tags>
            </div>`;
    }

    public async initSearch(): Promise<void> {
        const entryCount = this.entryCountFromQueryParameters();
        await this.searchFacade.search(entryCount);
        this.updateResultsText();
        this.initialized();
    }

    protected updateEntryCount(count: number): void {
        this.updateResultsText();
        this.updateMore(this.searchFacade.hasMoreEntries());
        this.queryParameters.set(ENTRY_COUNT_QUERY_PARAMETER_NAME, String(count));
    }

    private updateResultsText(): void {
        const count = this.searchFacade.displayableTotalCount();
        this.resultsText = this.calculateResultsText(count);
    }

    private calculateResultsText(displayableTotalCount: Nullable<number>): string {
        if (displayableTotalCount === null) {
            return this.dictionary.translate("CONTENT_SEARCH_LABEL_NO_SEARCH");
        }

        const subKey = displayableTotalCount === 1 ? "1" : "_";
        return this.dictionary.translate("CONTENT_SEARCH_RESULTS_LABEL", subKey, {[RESULT_COUNT_PLACEHOLDER]: displayableTotalCount});
    }

    protected updateMore(hasMore: boolean): void {
        this.hasMore = hasMore;
    }

    public async nextSearchPhrase(searchPhrase?: string): Promise<void> {
        await this.searchFacade.nextSearchPhrase(searchPhrase);
    }

    protected entryCountFromQueryParameters(): Maybe<number> {
        try {
            return this.queryParameters.getString(ENTRY_COUNT_QUERY_PARAMETER_NAME)?.toInt();
        } catch (e) {
            return undefined;
        }
    }
}